import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import DishEnergyDistributionChart from '../../components/DishEnergyDistributionChart';

const DishesNutritionInformation = ({ dish }) => {
    const { t } = useTranslation();
    
    // Comprehensive data validation and extraction
    const nutritions = Array.isArray(dish?.nutrients) ? dish.nutrients : [];
    const portionSize = dish?.portion_size || dish?.portion_size_g || dish?.portion_size_ml || 'N/A';

    // Robust helper function to format nutrition values
    const formatNutritionValue = (value, unit = '') => {
        if (value === null || value === undefined || value === '') return 'N/A';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return 'N/A';
        const formattedValue = numValue.toFixed(2);
        return unit ? `${formattedValue} ${unit}` : formattedValue;
    };

    // Robust helper function to calculate portion amount
    const calculatePortionAmount = (amountPer100g, portionSize) => {
        if (!amountPer100g || !portionSize || portionSize === 'N/A') return 'N/A';
        const amount = typeof amountPer100g === 'string' ? parseFloat(amountPer100g) : amountPer100g;
        const portion = typeof portionSize === 'string' ? parseFloat(portionSize) : portionSize;
        
        if (isNaN(amount) || isNaN(portion)) return 'N/A';
        
        const portionAmount = (amount * portion) / 100;
        return portionAmount.toFixed(2);
    };

    // Robust helper function to get nutrition name
    const getNutritionName = (nutrition) => {
        if (!nutrition) return 'N/A';
        return nutrition.name || nutrition.nutrient_name || nutrition.title || nutrition.label || 'N/A';
    };

    // Robust helper function to get nutrition amount per 100g
    const getNutritionAmountPer100g = (nutrition) => {
        if (!nutrition) return 'N/A';
        return nutrition.amount_per_100g || 
               nutrition.amount_per_100_g || 
               nutrition.per_100g || 
               nutrition.value_per_100g || 
               'N/A';
    };

    // Robust helper function to get nutrition unit
    const getNutritionUnit = (nutrition) => {
        if (!nutrition) return '';
        return nutrition.unit_abbr || 
               nutrition.unit || 
               nutrition.unit_abbreviation || 
               nutrition.unit_name || 
               '';
    };

    // Filter and sort nutrients like in ItemPage
    const getFilteredAndSortedNutrients = () => {
        if (!nutritions || nutritions.length === 0) return [];
        
        const priority = [
            "energi",
            "protein", 
            "fibrer",
            "fett",
            "mättat fett",
            "sockerarter",
            "kolhydrater",
            "salt",
            "kalium",
            "natrium",
            "fosfor",
        ];

        // Filter to only show nutrients in the priority list and validate data
        const filtered = nutritions.filter((n) => {
            if (!n) return false;
            const name = getNutritionName(n);
            const amount = getNutritionAmountPer100g(n);
            
            // Must have a valid name and amount
            if (name === 'N/A' || amount === 'N/A') return false;
            
            const nm = name.toString().trim().toLowerCase();
            return priority.includes(nm);
        });

        // Sort by priority order
        const sorted = filtered.sort((a, b) => {
            const an = getNutritionName(a).toString().trim().toLowerCase();
            const bn = getNutritionName(b).toString().trim().toLowerCase();
            return priority.indexOf(an) - priority.indexOf(bn);
        });

        return sorted;
    };

    const filteredNutrients = getFilteredAndSortedNutrients();

    // Robust validation - check if we have meaningful nutrition data
    const hasNutritionData = filteredNutrients.length > 0 && filteredNutrients.some(n => 
        n && getNutritionName(n) !== 'N/A' && getNutritionAmountPer100g(n) !== 'N/A'
    );

    // Don't render if no dish
    if (!dish) {
        return null;
    }

    const showNutritionTable = hasNutritionData;

    return (
        <div className="page-break-before-always px-4" style={{ pageBreakBefore: 'always' }}>
            
            <div className="grid grid-cols-1 gap-6">
                {/* Energy distribution chart (protein/carbs/fiber/fat -> kcal + %) */}
                <DishEnergyDistributionChart nutrients={nutritions} t={t} />

                {/* Nutrition Table */}
                {showNutritionTable ? (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden print:bg-white">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 print:bg-gray-50">
                            <h3 className="text-md font-medium text-gray-800">
                                {t("Näringsvärde")}
                            </h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 print:bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("Näringsämne")}
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("Per 100g")}
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t("Portion")} {portionSize}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200 print:bg-white">
                                    {filteredNutrients.map((nutrition, index) => {
                                        const name = getNutritionName(nutrition);
                                        const amountPer100g = getNutritionAmountPer100g(nutrition);
                                        const unit = getNutritionUnit(nutrition);
                                        const portionAmount = calculatePortionAmount(amountPer100g, portionSize);
                                        
                                        return (
                                            <tr key={nutrition.nutrient_id || nutrition.id || index} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {t(name)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                    {formatNutritionValue(amountPer100g, unit)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                    {formatNutritionValue(portionAmount, unit)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        <p>{t("Ingen näringsinformation tillgänglig")}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DishesNutritionInformation;
